import { useState, type FormEvent } from 'react';
import { ApiError, api } from './api.ts';
import { LegalLinks } from './legal/LegalLinks.tsx';
import { CONSOLE_PATH, LANDING_PATH, navigate } from './route.ts';
import './shell.css';
import './users.css';

/**
 * The way into the console, at `/signin` and nowhere else.
 *
 * It used to be what `/` rendered whenever nobody was signed in, which was fine
 * while the only visitor was an operator. It is not fine now that `/` is a page
 * addressed to strangers: a sign-in form is the wrong first thing to show
 * somebody who has not decided whether they want the product, and a form with
 * no address of its own cannot be linked to from an email either.
 *
 * A successful sign-in sets an httpOnly cookie the browser sends by itself.
 * This screen never sees a credential after the moment it posts one: there is
 * nothing to put in `localStorage`, nothing to read back, and nothing for an
 * injected script to steal. That is the whole reason the shared bearer token
 * this replaced had to go.
 *
 * **The console must not explain why a sign-in failed.** The server answers an
 * unknown address, a wrong password and a suspended account with the same body
 * on purpose — so that nobody can use this form to find out whether an address
 * has an account here, or whether someone has been suspended. Adding "check
 * your email address", or branching on anything in the response, would hand
 * back exactly what the server refused to say.
 */

/** Said for every rejected credential, whatever the actual reason was. */
const UNRECOGNISED = 'Those details were not recognised.';

/**
 * When to come back, in words rather than a number of seconds.
 *
 * Deliberately vague at the edges. The exact figure is not useful to a person
 * and reading it back precisely would tell an automated caller how long its
 * penalty is, which is the one audience this message is not for.
 */
const retryWording = (seconds: number | null): string => {
  if (seconds === null || seconds <= 0) return 'Wait a little while, then try again.';
  if (seconds <= 20) return 'Try again in a few seconds.';
  if (seconds < 90) return 'Try again in about a minute.';
  return `Try again in about ${Math.round(seconds / 60)} minutes.`;
};

const failureText = (cause: unknown): string => {
  if (cause instanceof ApiError) {
    if (cause.status === 429) {
      return `Too many sign-in attempts from this browser. ${retryWording(cause.retryAfter)}`;
    }
    if (cause.status === 401) return UNRECOGNISED;
    if (cause.status >= 500) return 'The server could not complete the sign-in. Try again shortly.';
    return cause.message;
  }
  // A thrown fetch is a transport failure: no server, no proxy, no network.
  return 'The console could not reach the server. Check your connection, then try again.';
};

/**
 * Why somebody has been sent back here, when they have been sent back here.
 *
 * A module-level handoff rather than a prop, because the two components
 * involved are no longer parent and child: the shell decides the session is
 * over and then navigates away from itself, so there is nothing left to pass a
 * prop through. It is read once and cleared, so a later visit to `/signin` —
 * from the landing page, from a bookmark — does not resurrect an explanation
 * for something that happened an hour ago.
 *
 * Not `sessionStorage`: this should not survive a reload, for the same reason.
 */
let pendingNotice: string | null = null;

export const setSignInNotice = (notice: string | null): void => {
  pendingNotice = notice;
};

const takeNotice = (): string | null => {
  const notice = pendingNotice;
  pendingNotice = null;
  return notice;
};

export const SignIn = () => {
  const [notice] = useState(takeNotice);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The notice explains how the person got back here. It is dismissed on the
   * first attempt: after that it is history, and leaving it above a fresh
   * failure stacks two explanations for two different things.
   */
  const [showNotice, setShowNotice] = useState(true);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setShowNotice(false);
    setError(null);
    try {
      await api.signIn(email, password);
      // The password is dropped the moment it has been posted. It is never put
      // in state that outlives the request, never logged, and never in a URL.
      setPassword('');
      /*
       * `replace`, not `push`. The browser's back button from inside the
       * console must not land on the sign-in form of a session that is now
       * live — the shell would immediately send them forward again, and a back
       * button that visibly does nothing reads as a broken page.
       *
       * The shell asks `/auth/me` for itself on mount, so the answer this call
       * returned is deliberately dropped rather than threaded across the
       * navigation: one source for who is signed in, and it is the server.
       */
      navigate(CONSOLE_PATH, { replace: true });
    } catch (cause) {
      setError(failureText(cause));
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      {/*
        The card and the way back are one block. `.signin` centres its children
        individually, so two siblings become two rows and the link lands at the
        foot of the viewport looking like part of a page that is not there.
      */}
      <div className="signin-panel">
        <div className="signin-card card">
        <div className="card-body">
          {/*
            The one h1 in the product that a screen owns. Every other screen
            renders inside the shell, whose header owns the heading; there is no
            shell here, and a page with no h1 is a page a screen reader cannot
            summarise.
          */}
          <h1>DriftWatch</h1>
          <p className="signin-lead">
            Sign in to the console. DriftWatch reads your clients&rsquo; Microsoft 365 tenants and
            can change nothing in any of them.
          </p>

          {notice !== null && showNotice && (
            <div className="callout callout-caution si-notice" role="status">
              {notice}
            </div>
          )}

          <form onSubmit={(event) => void submit(event)} noValidate>
            <div className="field">
              <label htmlFor="signin-email">Email address</label>
              <input
                id="signin-email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="off"
                spellCheck={false}
                required
                disabled={busy}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="signin-password">Password</label>
              <input
                id="signin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                disabled={busy}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            {/*
              `assertive`, not `polite`: this replaces what the person was doing
              rather than adding to it, and a polite region would wait behind
              whatever the browser was already saying about the form.
            */}
            <p aria-live="assertive" role="alert" className="si-error-live">
              {error !== null && <span className="error si-error">{error}</span>}
            </p>

            <button type="submit" className="btn btn-primary" aria-busy={busy || undefined} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          {/*
            No "forgot password". There is no reset flow, and a link that leads
            nowhere is worse than saying plainly what the actual route is.
          */}
          <p className="note si-help">
            There is no self-service password reset. If you cannot get in, an owner of your
            DriftWatch can set you a new password from Settings.
          </p>
          </div>
        </div>

        <p className="signin-back">
          <a
            href={LANDING_PATH}
            onClick={(event) => {
              // As on the landing page: modified clicks and middle clicks stay
              // with the browser, which is why this is a real href.
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate(LANDING_PATH);
            }}
          >
            Back to the DriftWatch home page
          </a>
        </p>

        {/*
          The terms are reachable from the screen that asks somebody to be bound
          by them. Nobody signing in is agreeing to anything at this moment, but
          this is where a person stands when they want to check what they agreed
          to, and sending them to the marketing page to find out is an answer
          that costs them a search.
        */}
        <LegalLinks label="Legal documents" className="legal-links signin-legal" />
      </div>
    </div>
  );
};
